<template>
    <div>
        <header class="text-center mb-4">
            <workflow-navigation workflowStage="Design" />
            <h2 class="skin-page-heading">Your retirement</h2>
            <p class="col-sm-8 mx-auto">
                Answer the questions and see if your plan works, if not, you don't need
                to go back, just hit solve to fix.
            </p>
        </header>

        <everything-you-want />

        <div class="row">
            <div class="col-lg-6">
                <decide-on-risk ref="decideOnRisk" />
                
                <scenario ref="scenario"
                          :scenario="ScenarioType.AccordingToPlan"
                          :solveEnabled="true"
                          :guaranteedIncomeEnabled="true"
                          :finishEncouragementEnabled="true"
                          @solve-take-less="onSolveTakeLess"
                          @solve-pay-more="onSolvePayMore"
                          @solve-equity-release="onSolveEquityRelease" />

            </div>
            <div class="col-lg-6">
                <income-and-pots />
                <leave-pots-untouched ref="leavePotsUntouched" />
            </div>
        </div>

        <page-nav backLink="income" @nextClicked="onNextClicked" :parent-will-handle-next="true" :next-link="nextLink" />

        <contact-us class="mt-5"></contact-us>
    </div>

  <modal
    ref="lastChanceSolveModal"
    title="Shortfall"    
    idKey="lastChanceSolveModal"
    key="lastChanceSolveModal"
    close-button-aria-label="Your plan has shortfalls and would be expected to run out of money if everything goes to plan. Do you want to try and solve these before leaving?"
  >
    <p>
      Your plan has shortfalls and would be expected to run out of money if
      everything goes to plan.
    </p>
    <p>Do you want to try and solve these before leaving?</p>
    <div class="mt-4 float-end">
      <solve-buttons
        @solve-take-less="onLastChanceSolveTakeLess"
        @solve-pay-more="onLastChanceSolvePayMore"
        @solve-equity-release="onLastChanceSolveEquityRelease"
        :scenario="ScenarioType.AccordingToPlan"
      />
      <button
        class="btn btn-secondary mx-3 px-4"
        @click="onProceedWithShortfallClick"
      >
        No
      </button>
    </div>
  </modal>

  <shortfall-solver ref="shortfallSolver" @closing="onShortfallSolverClosing" />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import DecideOnRisk from "@/guiide/components/design/DecideOnRisk.vue";
import LeavePotsUntouched from "@/guiide/components/design/LeavePotsUntouched.vue";
import EverythingYouWant from "@/guiide/components/design/EverythingYouWant.vue";
import IncomeAndPots from "@/guiide/components/charts/IncomeAndPots.vue";
import Scenario from "@/guiide/components/scenarios/Scenario.vue";
import ScenarioType from "../ts/scenarioType";
import ShortfallSolver from "@/guiide/components/scenarios/ShortfallSolver.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import ScenarioOutcome from "../ts/scenarioOutcome";
import Modal from "@/guiide/components/common/Modal.vue";
import SolveButtons from "@/guiide/components/scenarios/SolveButtons.vue";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";

export default defineComponent({
  name: "Design",
  components: {
    WorkflowNavigation,
    DecideOnRisk,
    LeavePotsUntouched,
    EverythingYouWant,
    IncomeAndPots,
    Scenario,
    ShortfallSolver,
    ContactUs,
    PageNav,
    Modal,
    SolveButtons
  },
  setup() {
    const decideOnRisk = ref(null);
    const leavePotsUntouched = ref(null);
    const scenario = ref(null);
    const shortfallSolver = ref(null);
    const lastChanceSolveModal = ref(null);
    const nextLink = ref("");

    return {
      decideOnRisk,
      leavePotsUntouched,
      scenario,
      shortfallSolver,
      ScenarioType,
      lastChanceSolveModal,
      nextLink
    };
  },
  async mounted() {
    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Design);
    this.nextLink = nextWorkflowStep.resource;
  },
  watch: {
    "$store.state.transactionOngoing"(val: boolean) {
      this.decideOnRisk.setDisabledState(val);
      this.leavePotsUntouched.setDisabledState(val);
      this.scenario.setDisabledState(val);
    }
  },
  methods: {
    onNextClicked() {
      if (
        this.$store.state.accordingToPlanScenario.outcome ==
        ScenarioOutcome.Fail
      ) {
        this.lastChanceSolveModal.showModal();
      } else {
        workflow.goToNextWorkflowStep();
      }
    },
    onSolveTakeLess() {
      this.shortfallSolver.checkCouldSolveByTakeLess(ScenarioType.AccordingToPlan);
    },
    onSolvePayMore() {
      this.shortfallSolver.checkCouldSolveByPayingMore(ScenarioType.AccordingToPlan);
    },
    onSolveEquityRelease() {
      this.shortfallSolver.checkCouldSolveByEquityRelease(ScenarioType.AccordingToPlan);
    },
    onLastChanceSolveTakeLess() {
      this.lastChanceSolveModal.closeModal();
      this.onSolveTakeLess();
    },
    onLastChanceSolvePayMore() {
      this.lastChanceSolveModal.closeModal();
      this.onSolvePayMore();
    },
    onLastChanceSolveEquityRelease() {
      this.lastChanceSolveModal.closeModal();
      this.onSolveEquityRelease();
    },
    onProceedWithShortfallClick() {
      this.lastChanceSolveModal.closeModal();
      workflow.goToNextWorkflowStep();
    },
    onShortfallSolverClosing() {
      this.scenario.takeFocus();
    }
  }
});
</script>
